// Home.js
import {
  Layout,
  Typography,
  Card,
  Button,
  Form,
  Input,
  Spin,
  Statistic,
  Descriptions,
  message,
} from "antd"; // Added 'message' for the verification success prompt
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { auth } from "../config/config-firebase";
import {
  onAuthStateChanged,
  signOut,
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber,
} from "firebase/auth";
import { jwtDecode } from "jwt-decode";
// import "../importData/Home.css"
import "../importData/Homepage.css";
import googlePlayBadge from "../images/appstores/google-play-badge.png";
import amazonAppstoreBadge from "../images/appstores/amazon-appstore-badge.png";
import samsungGalaxyBadge from "../images/appstores/samsung-galaxy-badge.png";
import rokuBadge from "../images/appstores/roku-badge.png";
import heroImage from "../images/picture1.png";
import logoImage from "../images/logo-black.png";
import customerLogo1 from "../images/customers/logo-1.png";
import customerLogo2 from "../images/customers/logo-2.png";
import customerLogo3 from "../images/customers/logo-3.png";
import customerLogo4 from "../images/customers/logo-4.png";
import customerLogo5 from "../images/customers/logo-5.png";
import customerLogo6 from "../images/customers/logo-6.png";
import customerLogo7 from "../images/customers/logo-7.png";
import customerLogo10 from "../images/customers/logo-10.png";
import customerLogo11 from "../images/customers/logo-11.png";
import arthurAveDeliLogo from "../images/customers/arthur-ave-deli.jpg";
import laPlacitaLogo from "../images/customers/la-placita.jpg";
import jgDeliLogo from "../images/customers/jg-deli-market.png";
import emilioPhoto from "../images/customers/emilio.jpg";
import axios from "axios";
import API_BASE from "../config/api";
import { onIdTokenChanged } from "firebase/auth";
import globalStore from "../global/globalStore.js";
import { resolveDefaultStoreCustomerId } from "../hooks/useMerchantStores";
import PricingPlans from "../components/PricingPlans";
import { SUBSCRIPTION_PLANS } from "../constants/plans";
import { render } from "@testing-library/react";

const HomeScreen = () => {
  const [user, setUser] = useState(null);
  const [jwt, setJwt] = useState("");
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [error, setError] = useState("");
  const [remainingTime, setRemainingTime] = useState(15 * 60); // Remaining time in seconds
  const navigate = useNavigate();
  const idleTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const [merchantName, setMerchantName] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [responsibleName, setResponsibleName] = useState("");
  const [mobile, setMobile] = useState("");
  const [phone, setPhone] = useState("");

  const [editPhone, setEditPhone] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const customerLogos = [
    customerLogo1,
    customerLogo2,
    customerLogo3,
    customerLogo4,
    customerLogo5,
    customerLogo6,
    customerLogo7,
    customerLogo10,
    customerLogo11,
    laPlacitaLogo,
    arthurAveDeliLogo,
    jgDeliLogo,
  ];

  const customerMarqueeItems = customerLogos.map((logo) => ({
    type: "logo",
    value: logo,
  }));

  const setupRecaptcha = () => {
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        callback: (response) => {},
      },
    );
  };

  const requestVerificationCode = async () => {
    setupRecaptcha();
    const phoneNumber = formatPhoneNumberToE164(newPhoneNumber);
    if (!phoneNumber) {
      setError("Invalid phone number format.");
      return;
    }

    try {
      const appVerifier = window.recaptchaVerifier;
      const provider = new PhoneAuthProvider(auth);
      // Store the verification ID globally or in state/ref to be used later
      const verificationId = await provider.verifyPhoneNumber(
        phoneNumber,
        appVerifier,
      );

      // Instead of window.prompt, update state to show verification input
      setShowVerificationInput(true);
      // Ensure 'message' is imported from 'antd' to use this
      message.info("Verification code sent! Please enter it below.");

      // Use a ref to store verificationId
      window.confirmationResult = verificationId;
    } catch (error) {
      console.error("Error during phone number verification:", error);
      setError(error.message);
    }
  };

  const submitVerificationCode = async () => {
    if (!verificationCode) {
      setError("Verification code not provided.");
      return;
    }

    if (!window.confirmationResult) {
      setError(
        "Verification process not started. Please request a code again.",
      );
      return;
    }

    try {
      const phoneCredential = PhoneAuthProvider.credential(
        window.confirmationResult,
        verificationCode,
      );
      await updatePhoneNumberForUser(phoneCredential);
    } catch (error) {
      console.error("Error submitting verification code:", error);
      setError("Invalid verification code. Please try again.");
    }
  };

  const updatePhoneNumberForUser = async (phoneCredential) => {
    if (auth.currentUser) {
      try {
        await updatePhoneNumber(auth.currentUser, phoneCredential); // Using modular approach for Firebase v9+
        console.log("Phone number updated successfully.");
        await updatePhoneNumberInBackend(
          formatPhoneNumberToE164(newPhoneNumber),
        );
        // Show success message
        message.success("Mobile number updated successfully!");
        cancelEditPhone();
      } catch (error) {
        console.error("Error updating phone number:", error);
        setError("Failed to update phone number: " + error.message);
      }
    } else {
      setError("No authenticated user.");
    }
  };

  const updatePhoneNumberInBackend = async (phoneNumber) => {
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch(`${API_BASE}/updateUserPhoneNumber`, {
        //http://localhost:8080/updateUserPhoneNumber
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: auth.currentUser.uid,
          newPhoneNumber: phoneNumber,
          token: idToken,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update phone number in backend.");
      }

      console.log("Phone number updated in backend successfully.");
      await fetchMerchantDetails(user.uid);
    } catch (error) {
      console.error("Backend update error:", error);
      setError("Failed to update phone number in backend.");
    }
  };

  const formatPhoneNumberToE164 = (number) => {
    const digits = number.replace(/\D/g, ""); // Strip all non-digits

    // Adjust based on your required country code (e.g., +1 for North America)
    if (digits.length === 10) {
      return `+1${digits}`; // Assumes a 10-digit number is US/Canada and prefixes +1
    } else if (digits.startsWith("1") && digits.length === 11) {
      return `+${digits}`; // Assumes 11 digits starting with '1' is already in the right format
    } else if (digits.startsWith("+")) {
      return digits; // Already looks like E.164
    } else {
      console.error(
        "Phone number format for E.164 conversion is unexpected:",
        number,
      );
      return null; // Or handle other country codes
    }
  };

  const startEditPhone = () => {
    setEditPhone(true);
    setPhoneNumberError("");
    // Note: 'mobile' is the formatted display string, you might want the raw E.164 version if available
    setNewPhoneNumber("");
  };

  const cancelEditPhone = () => {
    setEditPhone(false);
    setNewPhoneNumber("");
    setShowVerificationInput(false);
    setVerificationCode("");
    setError("");
    if (window.confirmationResult) {
      delete window.confirmationResult;
    }
  };

  // Adding state for editing states
  const [editState, setEditState] = useState({
    name: false,
    email: false,
    mobile: false, // Not used for phone, which uses editPhone state
  });

  // Function to toggle edit state

  const toggleEditState = (field) => {
    setEditState((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // Function to fetch merchant details
  const fetchMerchantDetails = async (id, isCustomerId = false) => {
    console.log("Fetching merchant details for ID:", id, "isCustomerId:", isCustomerId);
    try {
      const url = isCustomerId 
        ? `${API_BASE}/merchantDetailsByCustomer/${id}`
        : `${API_BASE}/merchantDetails/${id}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch merchant details: ${response.statusText}`,
        );
      }
      const data = await response.json();
      console.log("Fetched merchant details:", JSON.stringify(data, null, 2));

      // Backfill session phone for screens that gate on localStorage (e.g. /ai-menu); do not overwrite Login/Register
      const apiMobile = data.data?.mobile;
      if (apiMobile && !localStorage.getItem("phone")) {
        localStorage.setItem("phone", String(apiMobile).trim());
      }

      setMerchantDetails(data.data);
      setEmail(data.data.email || "");
      setAddress(data.data.address_line || "");
      setCity(data.data.town_city || "");
      setPostcode(data.data.postcode || "");
      setResponsibleName(data.data.name || "");
      // Assuming data.data.mobile is the E.164 format and needs formatting for display
      setMobile(formatPhoneNumber(data.data.mobile || ""));
      setPhone(formatPhoneNumber(data.data.phone || ""));
      setMerchantName(data.data.full_name || "");
      setDeliveryInstructions(data.data.delivery_instructions || "");
    } catch (error) {
      console.error("Error fetching merchant details:", error);
      setError(error.message);
    }
  };

  // Function to update merchant details
  const handleUpdate = async (e, fieldType) => {
    e.preventDefault();

    let updateData = {};
    switch (fieldType) {
      case "email":
        updateData = { responsiblePerson: { email } };
        break;
      case "name":
        updateData = { responsiblePerson: { name: responsibleName } };
        break;
      default:
        return;
    }

    // Check for UID before attempting to update
    if (!user || !user.uid) {
      setError("User not authenticated for update.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/updateMerchantDetails/${user.uid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update information");
      }

      // Success handling here
      console.log("Information updated successfully");
      message.success(
        `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} updated successfully!`,
      ); // Success message
      await fetchMerchantDetails(user.uid);
      toggleEditState(fieldType); // Turn off edit mode for the field
    } catch (error) {
      console.error("Error updating information:", error);
      setError(error.message || "Failed to update information");
    }
  };

  function formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return "";
    const digits = phoneNumber.replace(/\D/g, "");

    if (digits.length === 11 && digits.startsWith("1")) {
      // US/Canada E.164 format: +1NNNXXXXXXX
      const localNumber = digits.slice(1); // Remove the country code '1'
      return `(${localNumber.slice(0, 3)}) ${localNumber.slice(3, 6)}-${localNumber.slice(6)}`;
    } else if (digits.length === 10) {
      // Assumes a 10-digit number is US/Canada and formats
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else {
      // Return original if it doesn't match expected format
      return phoneNumber;
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      console.log("User signed out");
      navigate("/"); // Navigate to home
      window.location.reload();
    } catch (error) {
      console.error("Sign out error", error);
    }
  };

  const resetIdleTimer = () => {
    clearTimeout(idleTimeoutRef.current);
    clearInterval(countdownIntervalRef.current);
    setRemainingTime(15 * 60); // Reset remaining time to 15 minutes

    idleTimeoutRef.current = setTimeout(
      () => {
        alert("You have been logged out due to inactivity");
        handleSignOut();
      },
      15 * 60 * 1000,
    ); // 15 minutes

    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownIntervalRef.current); // Stop the countdown
          handleSignOut(); // Automatically sign out when the time is up
        }
        return prevTime - 1;
      });
    }, 1000); // Update remaining time every second
  };

  useEffect(() => {
    // Only attach event listeners if the user is authenticated (to prevent unnecessary checks on landing page)
    if (user) {
      document.addEventListener("mousemove", resetIdleTimer);
      document.addEventListener("keydown", resetIdleTimer);
    }

    return () => {
      document.removeEventListener("mousemove", resetIdleTimer);
      document.removeEventListener("keydown", resetIdleTimer);
      clearTimeout(idleTimeoutRef.current);
      clearInterval(countdownIntervalRef.current);
    };
  }, [user]); // Rerun effect when user state changes

  // useEffect(() => {
  //   // const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
  //   const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
  //     if (currentUser) {
  //       const idToken = await currentUser.getIdToken(true);
  //       console.log("Authenticated UID:", currentUser.uid);
  //       setUser(currentUser);
  //       setJwt(idToken);

  //       let jwtTemp = jwtDecode(idToken);

  //       const API_URL = `${API_BASE}/getCustomerId`;
  //       let params = jwtTemp.user_id.toString();

  //       resetIdleTimer();
  //       fetchMerchantDetails(currentUser.uid); // Fetch merchant details

  //       try {
  //         const response = await fetch(API_URL, {
  //           method: "POST",
  //           headers: {
  //             "Content-Type": "application/json",
  //           },
  //           body: JSON.stringify({ userId: params }),
  //         });

  //         const responseData = await response.json();

  //         console.log("responseData", responseData);
  //         if (response.ok && responseData.data?.customer_id) {
  //           globalStore.customerId = responseData.data.customer_id;
  //           localStorage.setItem("customer_id", responseData.data.customer_id);

  //           //
  //           const deviceId = localStorage.getItem("pending_device_id");
  //           if (deviceId) {
  //             console.log("Device login triggered");
  //             const customerId = localStorage.getItem("customer_id");
  //             console.log("customerId", customerId);
  //             const deviceResponse = await axios.post(
  //               `${API_BASE}/device_login`,
  //               {
  //                 customer_id: customerId,
  //                 device_id: deviceId,
  //               },
  //             );
  //             console.log("Device login response:", deviceResponse.data);
  //             if (deviceResponse.status === 200) {
  //               navigate("/modes");
  //               localStorage.removeItem("pending_device_id");
  //             }
  //           }
  //         } else {
  //           console.error(
  //             "Get user info failed: Customer ID not found.",
  //             responseData,
  //           );
  //         }
  //       } catch (error) {
  //         console.error("Error submitting registration data:", error);
  //       }
  //     } else {
  //       // Redirect to login if there is no user
  //       setUser(null);
  //       setJwt("");
  //       // Don't auto-navigate if the user is on the root path, let AuthPrompt handle it
  //       if (window.location.pathname !== "/") {
  //         navigate("/");
  //       }
  //     }
  //   });

  //   return () => unsubscribe();
  // }, [navigate]);

  const isCalledRef = useRef(false); // ✅ prevents duplicate execution across renders

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
      const customerId = localStorage.getItem("customer_id");

      if (!currentUser?.uid) {
        setUser(null);
        setJwt("");

        if (customerId) {
          // ✅ prevent multiple API calls
          if (isCalledRef.current) return;
          isCalledRef.current = true;

          // ✅ Support promo login session
          console.log("✅ Promo login detected in Home:", customerId);
          setUser({ uid: customerId });
          fetchMerchantDetails(customerId, true);
          return;
        }

        if (window.location.pathname !== "/") {
          navigate("/");
        }
        return;
      }

      // ✅ prevent multiple API calls (VERY IMPORTANT)
      if (isCalledRef.current) return;
      isCalledRef.current = true;

      // ✅ If registration is still in progress, wait for it to finish
      const waitForRegistration = () => {
        return new Promise((resolve) => {
          const isInProgress = localStorage.getItem("registration_in_progress");
          if (!isInProgress) {
            resolve(); // Not registering, proceed immediately
            return;
          }

          console.log("Waiting for registration to complete...");
          // Poll every 300ms until the flag is gone
          const interval = setInterval(() => {
            if (!localStorage.getItem("registration_in_progress")) {
              clearInterval(interval);
              resolve();
            }
          }, 300);

          // Safety timeout after 15 seconds
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 15000);
        });
      };

      try {
        await waitForRegistration(); // ✅ Wait here if registration is ongoing
        // ✅ do NOT force refresh
        const idToken = await currentUser.getIdToken();

        setUser(currentUser);
        setJwt(idToken);

        const jwtTemp = jwtDecode(idToken);
        const params = jwtTemp.user_id.toString();

        resetIdleTimer();

        // ✅ ensure correct sequence
        await fetchMerchantDetails(currentUser.uid);

        // ---- Get Customer ID ----
        const response = await fetch(`${API_BASE}/getCustomerId`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: params }),
        });

        const responseData = await response.json();

        if (response.ok && responseData.data?.customer_id) {
          globalStore.customerId = responseData.data.customer_id;
          localStorage.setItem("customer_id", responseData.data.customer_id);
          console.log("✅ Customer ID stored:", responseData.data.customer_id);

          const deviceId = localStorage.getItem("pending_device_id");

          if (deviceId) {
            // Resolve the DEFAULT store's customer_id — never fall back to
            // getCustomerId's account-level value, that's the wrong id for
            // this call. If it can't be resolved, leave pending_device_id
            // in place so the next login retries instead of linking the
            // device to the wrong (or a made-up) store.
            const customerId = await resolveDefaultStoreCustomerId(currentUser.uid);

            if (!customerId) {
              console.error(
                "Could not resolve a store customer_id for device_login; leaving pending_device_id for retry on next login.",
              );
            } else {
              const deviceResponse = await axios.post(
                `${API_BASE}/device_login`,
                {
                  customer_id: customerId,
                  device_id: deviceId,
                },
              );

              if (deviceResponse.status === 200) {
                navigate("/modes");
                localStorage.removeItem("pending_device_id");
              }
            }
          }
        } else {
          console.error("Customer ID not found", responseData);
        }
      } catch (error) {
        console.error("Auth flow error:", error);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Convert remaining time to minutes and seconds for display
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const AuthPrompt = ({ navigate }) => {
    return (
      // <div className="home-container">
      //   <div className="auth-card">
      //     <h2 className="auth-title">Welcome to Ezwinds</h2>
      //     <p className="auth-subtitle">
      //       Please log in to access your account or create a new one.
      //     </p>
      //     <div className="auth-buttons">
      //       <button
      //         onClick={() => navigate("/login")}
      //         className="btn login"
      //       >
      //         Log In
      //       </button>
      //       <button
      //         onClick={() => navigate("/register")}
      //         className="btn register"
      //       >
      //         Register
      //       </button>
      //     </div>

      //
      //     <div className="contact-section">
      //       <p className="contact-text">
      //         Contact Us:{" "}
      //         <a href="mailto:info@ezwinds.com" className="contact-link">
      //          info@ezwinds.com
      //         </a>
      //       </p>
      //     </div>
      //   </div>
      // </div>

      // LOGIN END

      <main className="min-h-screen newHomeContainer">
        <div className="landHeader">
          <div className="container px-4 ">
            <div className="logoWraps">
              <span className="txtLogo font-bold">Ezwinds</span>
            </div>
            <div className="btnWraps">
              <Button
                size="lg"
                variant="hero"
                className="text-base logBtns"
                onClick={() => navigate("/login")}
              >
                Log In{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-arrow-right ml-2 h-5 w-5"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </Button>
              <Button
                size="lg"
                variant="heroOutline"
                className="text-base logBtns ml_space"
                onClick={() => navigate("/register")}
              >
                Register{" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-arrow-right ml-2 h-5 w-5"
                >
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </Button>
            </div>
          </div>
        </div>

        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-hero-gradient"></div>

          {/* Animated Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-radial from-primary/20 via-transparent to-transparent animate-glow"></div>

          <div className="container relative z-10 px-4 py-20 md:py-32 padTopss">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="lg:text-left space-y-8 animate-fade-in">
                <div className="inline-block">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 backdrop-blur-sm border border-accent/20 text-accent text-sm font-medium topsBoxss">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                    </span>
                    Transform Your Retail Experience
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight">
                  Digital Advertising
                  <span className="block bg-accent-gradient bg-clip-text text-transparent spceRd">
                    Powered by TV
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl  lg:mx-0">
                  Transform any TV into a powerful digital advertisement
                  platform. Engage customers with dynamic content that drives
                  sales and enhances in-store experience.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 lg:justify-start heroBtns">
                  <Button
                    size="lg"
                    variant="heroOutline"
                    className="text-base  regBtns"
                    onClick={() => navigate("/register")}
                  >
                    Register Now{" "}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-arrow-right ml-2 h-5 w-5"
                    >
                      <path d="M5 12h14"></path>
                      <path d="m12 5 7 7-7 7"></path>
                    </svg>
                  </Button>
                  <a
                    size="lg"
                    variant="hero"
                    className="text-base logBtns"
                    target="_blank"
                    href="https://www.youtube.com/watch?v=P_XcFfDN4fY"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-play mr-2 h-5 w-5"
                    >
                      <polygon points="6 3 20 12 6 21 6 3"></polygon>
                    </svg>
                    Watch Demo
                  </a>
                </div>

                {/* <div className="flex items-center gap-8 justify-center lg:justify-start pt-4">
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-primary-foreground">500+</div>
                <div className="text-sm text-primary-foreground/70">Active Stores</div>
              </div>
              <div className="h-12 w-px bg-primary-foreground/20"></div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-primary-foreground">2M+</div>
                <div className="text-sm text-primary-foreground/70">Daily Views</div>
              </div>
              <div className="h-12 w-px bg-primary-foreground/20"></div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-primary-foreground">98%</div>
                <div className="text-sm text-primary-foreground/70">Uptime</div>
              </div>
            </div> */}
              </div>

              {/* Right Content - Hero Image */}
              <div className="relative animate-fade-in-up">
                <div className="relative rounded-2xl overflow-hidden shadow-glow">
                  <img
                    src={heroImage}
                    alt="Digital in-store advertising displays showing dynamic content on TV screens"
                    className="w-full h-auto rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent"></div>
        </section>

        {/* APPSTORE START */}
        <section className="py-8 md:py-12 bg-muted/30 border-y border-border">
          <div className="container px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
              <div className="flex flex-wrap items-stretch justify-center gap-6">
                <a
                  href="https://play.google.com/store/apps/details?id=com.ezwinds.tvads&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={googlePlayBadge}
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>

                <a
                  href="https://www.amazon.com/Ezwinds/dp/B0G2TNMZPY?dplnkId=1b896e14-b498-4786-bd49-d265c4494f63&nodl=1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={amazonAppstoreBadge}
                    alt="Available at Amazon Appstore"
                    className="h-12 w-auto"
                  />
                </a>

                <a
                  href="https://www.samsung.com/us/tvs/smart-tv/samsung-tv-apps-detail/?appId=3202510041996"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={samsungGalaxyBadge}
                    alt="Available on Galaxy Store"
                    className="h-12 w-auto"
                  />
                </a>

                <a
                  href="https://channelstore.roku.com/details/79fd4a255cc2d43e50374647a3eb3c85:8e28e001efab5a1a78f26e7a68e31230/ezwinds"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={rokuBadge}
                    alt="Available on the Roku Channel Store"
                    className="h-12 w-auto"
                  />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* APPSTORE END */}

        {/* OUR CUSTOMERS START */}
        <section className="py-16 md:py-24 bg-secondary/20 relative overflow-hidden">
          <div className="container px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-10 animate-fade-in">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Our Customers
              </h2>
              <p className="text-lg text-muted-foreground">
                Trusted by local businesses across New York
              </p>
            </div>

            <div className="relative overflow-hidden">
              <div
                className="flex items-center gap-4 md:gap-6 w-max"
                style={{
                  animation: "customerLogosMarquee 38s linear infinite",
                }}
              >
                {[...customerMarqueeItems, ...customerMarqueeItems].map((item, index) => (
                  <div
                    key={`${item.value}-${index}`}
                    className="bg-white border border-border rounded-xl shadow-sm flex items-center justify-center p-2"
                    style={{ width: "6cm", height: "2.7cm" }}
                  >
                    {item.type === "logo" ? (
                      <img
                        src={item.value}
                        alt="Customer logo"
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-xl font-bold text-foreground text-center leading-tight px-2">
                        {item.value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <style>
            {`
              @keyframes customerLogosMarquee {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}
          </style>
        </section>
        {/* OUR CUSTOMERS END */}

        {/* TESTIMONIAL START */}
        <section className="py-16 md:py-24 bg-background relative overflow-hidden">
          <div className="container px-4 relative z-10">
            <div className="max-w-4xl mx-auto bg-white border border-border rounded-3xl shadow-sm p-6 md:p-10 flex flex-col md:flex-row items-center gap-8 md:gap-12 animate-fade-in">
              <img
                src={emilioPhoto}
                alt="Emilio Dominguez, Gordo's Deli II"
                className="w-40 h-40 md:w-56 md:h-56 rounded-full object-cover flex-shrink-0 shadow-md"
                loading="lazy"
              />
              <figure className="text-center md:text-left">
                <span
                  aria-hidden="true"
                  className="block text-6xl leading-none text-primary/30 font-serif"
                >
                  &ldquo;
                </span>
                <blockquote className="text-2xl md:text-3xl font-semibold text-foreground leading-snug">
                  Ezwinds know what customers really want
                </blockquote>
                <figcaption className="mt-5 text-lg text-muted-foreground">
                  <span className="font-semibold text-foreground">Emilio Dominguez</span>
                  <span className="block text-base">Gordo&apos;s Deli II</span>
                </figcaption>
              </figure>
            </div>
          </div>
        </section>
        {/* TESTIMONIAL END */}

        {/* SECTION-ONE START */}
        <section className="py-20 pb-0 md:py-32 bg-background relative">
          <div className="container px-4">
            <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Everything You Need for
                <span className="block bg-accent-gradient bg-clip-text text-transparent spceRd">
                  Digital Advertising
                </span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Powerful features designed to help you engage customers and
                drive sales through dynamic in-store advertising.
              </p>
            </div>
          </div>
        </section>
        {/* SECTION-ONE END */}

        {/* SECTION-TWO START */}
        <section className="pb-20 pt-0 md:py-32 bg-background relative">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-tv h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <rect
                      width="20"
                      height="15"
                      x="2"
                      y="7"
                      rx="2"
                      ry="2"
                    ></rect>
                    <polyline points="17 2 12 7 7 2"></polyline>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  TV-First Platform
                </h3>
                <p className="text-muted-foreground">
                  Transform any smart TV or display into a powerful advertising
                  medium with plug-and-play simplicity.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-zap h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  Real-Time Updates
                </h3>
                <p className="text-muted-foreground">
                  Update content instantly across all your displays. Schedule
                  campaigns and manage them from anywhere.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-chart-column h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <path d="M3 3v16a2 2 0 0 0 2 2h16"></path>
                    <path d="M18 17V9"></path>
                    <path d="M13 17V5"></path>
                    <path d="M8 17v-3"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  Analytics Dashboard
                </h3>
                <p className="text-muted-foreground">
                  Track engagement, views, and performance metrics. Make
                  data-driven decisions for your campaigns.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-cloud h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  Cloud-Based
                </h3>
                <p className="text-muted-foreground">
                  Control all your displays from your phone or computer. Update
                  content on the go, anytime.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-zap h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  Remote Management
                </h3>
                <p className="text-muted-foreground">
                  Control all your displays from your phone or computer. Update
                  content on the go, anytime.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-glow transition-all duration-300 group bg-card border-border animate-fade-in-up">
                <div className="mb-4 inline-flex p-3 rounded-lg bg-primary/10 group-hover:bg-accent-gradient transition-all duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-shield h-6 w-6 text-primary group-hover:text-accent-foreground transition-colors"
                  >
                    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-card-foreground">
                  Enterprise Security
                </h3>
                <p className="text-muted-foreground">
                  Bank-level encryption and security. Your content and data are
                  always protected.
                </p>
              </Card>
            </div>
          </div>
        </section>
        {/* SECTION-TWO END */}

        {/* SECTION-THREE START */}
        <section className="py-20 md:py-32 bg-secondary/30 relative overflow-hidden bgBlues">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)]"></div>

          <div className="container px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Get Started in Minutes
              </h2>
              <p className="text-lg text-muted-foreground">
                Our streamlined process makes it easy to launch your digital
                advertising campaigns.
              </p>
            </div>

            <div className="relative animate-fade-in-up">
              <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="hidden lg:block bgBars absolute top-16 left-[60%] w-full h-0.5 bg-gradient-to-r from-primary to-accent/30"></div>

                <div className="relative bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all duration-300 group h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-accent-gradient text-accent-foreground font-bold text-lg flex items-center justify-center shadow-lg">
                    01
                  </div>

                  <div className="mb-4 inline-flex p-3 rounded-xl bg-primary/10 group-hover:scale-110 transition-transform duration-300">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-upload h-8 w-8 text-primary"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" x2="12" y1="3" y2="15"></line></svg>*/}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-user-plus h-8 w-8 text-primary"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <line x1="19" y1="8" x2="19" y2="14"></line>
                      <line x1="22" y1="11" x2="16" y2="11"></line>
                    </svg>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 text-card-foreground">
                    {/* Upload Your Content */}
                    Register
                  </h3>
                  <p className="text-muted-foreground">
                    {/* Upload videos, images, or create dynamic campaigns directly in our intuitive dashboard. */}
                    Create your account and set up your business profile to get
                    started with our platform.
                  </p>
                </div>
                {/* Grid-1 */}

                <div className="relative bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all duration-300 group h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-accent-gradient text-accent-foreground font-bold text-lg flex items-center justify-center shadow-lg">
                    02
                  </div>

                  <div className="mb-4 inline-flex p-3 rounded-xl bg-primary/10 group-hover:scale-110 transition-transform duration-300">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-settings h-8 w-8 text-primary"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></svg> */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-tags h-8 w-8 text-primary"
                    >
                      <path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19"></path>
                      <path d="M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z"></path>
                      <circle
                        cx="6.5"
                        cy="9.5"
                        r=".5"
                        fill="currentColor"
                      ></circle>
                    </svg>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 text-card-foreground">
                    {/* Configure Display */}
                    Add Promotions
                  </h3>
                  <p className="text-muted-foreground">
                    {/* Set up your TV displays and organize them by location, schedule, or campaign type. */}
                    Upload your promotional content, videos, and campaigns
                    through our easy-to-use dashboard.
                  </p>
                </div>
                {/* Grid-2 */}

                <div className="relative bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all duration-300 group h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-accent-gradient text-accent-foreground font-bold text-lg flex items-center justify-center shadow-lg">
                    03
                  </div>

                  <div className="mb-4 inline-flex p-3 rounded-xl bg-primary/10 group-hover:scale-110 transition-transform duration-300">
                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-tv-minimal h-8 w-8 text-primary"><path d="M7 21h10"></path><rect width="20" height="14" x="2" y="3" rx="2"></rect></svg> */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-monitor-play h-8 w-8 text-primary"
                    >
                      <path d="M10 7.75a.75.75 0 0 1 1.142-.638l3.664 2.249a.75.75 0 0 1 0 1.278l-3.664 2.25a.75.75 0 0 1-1.142-.64z"></path>
                      <path d="M12 17v4"></path>
                      <path d="M8 21h8"></path>
                      <rect x="2" y="3" width="20" height="14" rx="2"></rect>
                    </svg>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 text-card-foreground">
                    {/* Go Live */}
                    Login Through TV App
                  </h3>
                  <p className="text-muted-foreground">
                    {/* Your content automatically starts playing on all configured displays in real-time. */}
                    Install our TV app on your display devices and login to
                    start showing your promotions instantly.
                  </p>
                </div>
                {/* Grid-3 */}

                {/* <div className="relative bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all duration-300 group h-full">
                  <div className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-accent-gradient text-accent-foreground font-bold text-lg flex items-center justify-center shadow-lg">
                    04
                  </div>

                  <div className="mb-4 inline-flex p-3 rounded-xl bg-primary/10 group-hover:scale-110 transition-transform duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-up h-8 w-8 text-primary"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 text-card-foreground">Track & Optimize</h3>
                  <p className="text-muted-foreground">Monitor performance metrics and optimize your campaigns for maximum engagement.</p>
                </div> */}
                {/* Grid-4 */}
              </div>
            </div>
          </div>
        </section>
        {/* SECTION-THREE END */}

        {/* PRICING START */}
        <section className="py-20 md:py-32 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(59,130,246,0.05),transparent_50%)]"></div>

          <div className="container px-4 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg text-muted-foreground">
                Choose the plan that fits your business needs. Start free and
                upgrade as you grow.
              </p>
            </div>

            <PricingPlans
              plans={SUBSCRIPTION_PLANS}
              renderAction={(plan) => (
                <Button className={plan.buttonClassName} size="lg">
                  {plan.ctaLabel}
                </Button>
              )}
            />
          </div>
        </section>
        {/* PRICING END */}

        {/* SECTION-FOUR START */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          <div className="absolute inset-0 bg-hero-gradient"></div>
          <div className="absolute inset-0 bg-gradient-radial from-accent/20 via-transparent to-transparent"></div>

          <div className="container px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground">
                Ready to Transform Your
                <span className="block bg-accent-gradient bg-clip-text text-transparent spceRd">
                  In-Store Experience?
                </span>
              </h2>

              <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto">
                Join hundreds of retailers already using our platform to engage
                customers and boost sales through dynamic digital advertising.
              </p>
              {/*           
                  <div className="flex flex-wrap gap-4 justify-center pt-4">
                  
                      <div className="flex items-center gap-2 text-primary-foreground/90" >             
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check-big h-5 w-5 text-accent"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                        <span className="text-sm font-medium">Lorem ipsum</span>
                      </div>
                      <div className="flex items-center gap-2 text-primary-foreground/90" >             
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check-big h-5 w-5 text-accent"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                        <span className="text-sm font-medium">Lorem ipsum</span>
                      </div>
                      <div className="flex items-center gap-2 text-primary-foreground/90" >             
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check-big h-5 w-5 text-accent"><path d="M21.801 10A10 10 0 1 1 17 3.335"></path><path d="m9 11 3 3L22 4"></path></svg>
                        <span className="text-sm font-medium">Lorem ipsum</span>
                      </div>
                    
                  </div> */}

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  size="lg"
                  variant="heroOutline"
                  className="text-base logBtns w-200"
                  onClick={() => navigate("/register")}
                  style={{ fontSize: "15px" }}
                >
                  Register Now{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-arrow-right ml-2 h-5 w-5"
                  >
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        </section>
        {/* SECTION-FOUR END */}

        {/* FOOTER Start*/}
        <footer className="bg-card border-t border-border pt-4">
          <div className="container px-4 pt-5 py-2 md:py-16">
            <div className="ftrGrid grid md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
              {/* Brand Column */}
              <div className="lg:col-span-2 logoParts">
                <div className="flex items-center gap-2 mb-3">
                  {/* <span className="text-xl font-bold text-foreground txtLogo">Ezwinds</span> */}
                  <img src={logoImage} alt="logo" className="logoImg" />
                </div>
                <p>
                  Transform your retail space with powerful digital advertising
                  solutions that engage customers and drive sales
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-mail h-4 w-4"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                    </svg>
                    <span>
                      <a href="mailto:info@ezwinds.com">info@ezwinds.com</a>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-phone h-4 w-4"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <span>
                      <a href="tel:+12073705346">+1 207 370 5346</a>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-map-pin h-4 w-4"
                    >
                      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>New York, US</span>
                  </div>
                </div>
              </div>

              {/* Links Columns */}
              <div className="ftrLinks">
                <h3 className="ftTitles">Product</h3>
                <ul className="space-y-2">
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Features
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Pricing
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      FAQ
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Demo
                    </a>
                  </li>
                </ul>
              </div>

              {/* Links Columns */}
              <div className="ftrLinks">
                <h3 className="ftTitles">Company</h3>
                <ul className="space-y-2">
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Careers
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Blog
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Press Kit
                    </a>
                  </li>
                </ul>
              </div>

              {/* Links Columns */}
              <div className="ftrLinks">
                <h3 className="ftTitles">Resources</h3>
                <ul className="space-y-2">
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Documentation
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      API Reference
                    </a>
                  </li>
                  <li>
                    <a
                      href="/support"
                      className="text-muted-foreground hover:text-primary transition-colors text-sm"
                    >
                      Support
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Links Columns */}
              <div className="ftrLinks">
                <h3 className="ftTitles">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="/privacy-policy"
                      className="text-muted-foreground hover:text-primary transition-colors text-sm"
                    >
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a
                      href="/copyright-policy"
                      className="text-muted-foreground hover:text-primary transition-colors text-sm"
                    >
                      Copyright Policy
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      Cookie Policy
                    </a>
                  </li>
                  <li>
                    <a className="text-muted-foreground hover:text-primary transition-colors text-sm">
                      GDPR
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-border mt-4 py-4">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 ftrCopyright">
                <p className="text-sm text-muted-foreground my-1">
                  © 2025 Ezwinds. All rights reserved.
                </p>
                <div className="flex items-center gap-6 socilIcons">
                  <a
                    href="#"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span className="sr-only">Twitter</span>
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <span className="sr-only">LinkedIn</span>
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* FOOTER End*/}
      </main>
    );
  };

  if (!user) return <AuthPrompt navigate={navigate} />;

  return <Navigate to="/home" replace />;
};

const styles = {
  card: {
    marginTop: 16,
    width: "100%",
  },
  title: {
    marginBottom: "20px",
    color: "#227722",
  },
  button: {
    backgroundColor: "#338833",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "5px",
    marginTop: "10px",
    cursor: "pointer",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
  label: {
    textAlign: "left",
    width: "100%",
    margin: "10px 0",
  },
  input: {
    margin: "10px 0",
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    width: "95%",
  },
  descriptionLabel: {
    fontWeight: "bold",
    textAlign: "right",
    paddingRight: "20px",
    width: "30%",
  },
  descriptionContent: {
    textAlign: "left",
    width: "70%",
  },
};

export default HomeScreen;
